import dayjs from "dayjs";
import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type {
  Education,
  EducationStatus,
} from "@/features/education-management/types/education-management.types";
import type {
  UpcomingTraining,
  UpcomingTrainingStatus,
  DashboardAnnouncement,
  DashboardAnnouncementsParams,
  PaginatedResponse,
  DashboardSummaryApiResponse,
  DashboardSummary,
  DashboardEducationStatus,
  DashboardCategorySuccessCompletion,
} from "../types/dashboard.types";

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * BE EducationStatus → widget UpcomingTrainingStatus.
 * Yalnızca "yaklaşan" sayılan statüler eşlenir; ARCHIVED (tamamlandi) ve DRAFT
 * (taslak) liste sorgusunda filtrelendiği için burada yer almaz.
 */
const EDUCATION_STATUS_TO_WIDGET: Partial<
  Record<EducationStatus, UpcomingTrainingStatus>
> = {
  aktif: "active",
  talepToplama: "request_collection",
  kapali: "closed",
  butceBekliyor: "budget_pending",
};

/** "Yaklaşan Eğitimler" tablosunda gösterilen statüler (ARCHIVED/DRAFT hariç). */
const UPCOMING_TRAINING_STATUSES: EducationStatus[] = [
  "talepToplama",
  "butceBekliyor",
  "aktif",
  "kapali",
];

/** Dashboard widget'ı yalnızca en yakın N eğitimi gösterir (sayfalama yok). */
const UPCOMING_TRAINING_LIMIT = 10;

/** Admin eğitim listesi kaydını dashboard "Yaklaşan Eğitimler" satırına çevirir. */
function mapEducationToUpcoming(edu: Education): UpcomingTraining {
  // Gerçek başlangıç tarihi: önce sınıf (VirtualClassroom) başlangıcı — çünkü
  // Education.startDate eğitim yayına alınınca yayın zamanıyla eziliyor, dolayısıyla
  // gerçek ders başlangıcı değil. Sınıf yoksa publishDate (= startDate ?? createdAt)
  // yedeğe düşer.
  const startSource = edu.classStartDate ?? edu.publishDate;
  return {
    id: edu.id,
    trainingName: edu.educationName,
    format: i18n.t(`educationManagement.formatOptions.${edu.format}`),
    registrationCount: edu.registrationCount,
    maxRegistrations:
      edu.maxRegistration != null && edu.maxRegistration > 0
        ? edu.maxRegistration
        : null,
    publicationDate: startSource
      ? dayjs(startSource).format("DD.MM.YYYY")
      : "-",
    status: EDUCATION_STATUS_TO_WIDGET[edu.status] ?? "active",
  };
}

const MOCK_ANNOUNCEMENTS: DashboardAnnouncement[] = [
  {
    id: "1",
    imageUrl: "https://picsum.photos/seed/d1/80/80",
    title: i18n.t("mockData.announcement.systemMaintenance"),
    time: "13:45",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "2",
    imageUrl: "https://picsum.photos/seed/d2/80/80",
    title: i18n.t("mockData.announcement.osgReminder"),
    time: "21:32",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
  {
    id: "3",
    imageUrl: "https://picsum.photos/seed/d3/80/80",
    title: i18n.t("mockData.announcement.libraryCatalog"),
    time: "12.02.2026",
    tag: "Yeni Katalog",
    tagType: "catalog",
  },
  {
    id: "4",
    imageUrl: "https://picsum.photos/seed/d4/80/80",
    title: i18n.t("mockData.announcement.yearlyPlan"),
    time: "11.02.2026",
    tag: "Eğitim",
    tagType: "education",
  },
  {
    id: "5",
    imageUrl: "https://picsum.photos/seed/d5/80/80",
    title: i18n.t("mockData.announcement.keypUpdate"),
    time: "10.02.2026",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "6",
    imageUrl: "https://picsum.photos/seed/d6/80/80",
    title: i18n.t("mockData.announcement.certRenewal"),
    time: "09.02.2026",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
];

async function getDashboardSummary(params?: {
  /** Chart verilerini son N gün ile sınırla (BE desteği beklendiğinde aktifleşir). */
  periodDays?: number;
}): Promise<{
  summary: DashboardSummary;
  educationStatus: DashboardEducationStatus;
  categorySuccessCompletion: DashboardCategorySuccessCompletion;
}> {
  const { data } = await apiClient.get<DashboardSummaryApiResponse>(
    "/api/admin/dashboard",
    { params: params?.periodDays ? { periodDays: params.periodDays } : undefined },
  );

  if (!data?.data) {
    throw new Error("Dashboard summary not found");
  }

  return data.data;
}

export const dashboardApi = {
  /**
   * GET /api/admin/dashboard - top summary + charts for admin dashboard.
   */
  getDashboardSummary,
  /**
   * Get upcoming trainings with pagination (for dashboard table)
   */
  getUpcomingTrainings: async (): Promise<UpcomingTraining[]> => {
    const res = await educationManagementApi.getAll({
      page: 1,
      pageSize: UPCOMING_TRAINING_LIMIT,
      // Efektif başlama tarihine göre sırala (sınıf başlangıcı ?? startDate ??
      // createdAt) — BE'de hesaplanır. En yakın/yeni tarih en üstte.
      sortBy: "effectiveStartDate",
      sortOrder: "desc",
      filters: { status: UPCOMING_TRAINING_STATUSES },
    });
    return res.data.map(mapEducationToUpcoming);
  },

  /**
   * Get announcements for dashboard list (general/private tab, paginated)
   */
  getAnnouncements: async (
    params?: DashboardAnnouncementsParams
  ): Promise<PaginatedResponse<DashboardAnnouncement>> => {
    await delay(300);
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 5;
    const tab = params?.tab ?? "general";
    let list = MOCK_ANNOUNCEMENTS;
    if (tab === "private") {
      list = MOCK_ANNOUNCEMENTS.filter((a) => a.tagType === "reminder");
    }
    const start = (page - 1) * pageSize;
    const data = list.slice(start, start + pageSize);
    return {
      data,
      total: list.length,
      page,
      pageSize,
    };
  },
};
